import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * The commission allocation line and its "arrived" figure, shared by the Commissions report and
 * the invoice detail (ATA part 2 §3c): Arrived is never blank. While an invoice is unpaid the
 * amount still expected shows in the brand's status red; once fully paid the figure turns status
 * green. The money-blind administrator gets a dash, never a number.
 */

export type CommissionStatus = 'Mailed' | 'Paid' | 'Void' | 'NonBillable' | 'Commission Paid';

export interface CommissionAllocation {
  id: string; role: string; role_label?: string; assignee_type: 'user' | 'contact' | 'house';
  user_id: string | null; assignee_name: string; percent: number;
  amount_invoiced?: number | null; amount_arrived?: number | null; amount_expected?: number | null;
}

/** Bucket names, mirroring ROLE_LABELS in services/commission-allocations.js; the invoice
 *  splits endpoint returns the bare role, the report returns it already labelled. */
export const ROLE_LABELS: Record<string, string> = { agent: 'Agent', manager: 'Manager', originator: 'Originator', commission: 'Commission' };
export const roleLabel = (a: { role: string; role_label?: string }) => a.role_label || ROLE_LABELS[a.role] || a.role;

export function money(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

/** PTS status vocabulary for an invoice row, mirroring services/commission-report.js. */
export function commissionStatusOf(inv: { status?: string | null; commission_paid_at?: string | null }): CommissionStatus {
  if (inv.status === 'void' || inv.status === 'cancelled') return 'Void';
  if (inv.commission_paid_at) return 'Commission Paid';
  if (inv.status === 'paid') return 'Paid';
  return 'Mailed';
}

export function ArrivedCell({ a, status }: { a: CommissionAllocation; status: CommissionStatus }) {
  if (a.amount_invoiced == null || a.amount_arrived == null) return <span>—</span>;
  const settled = status === 'Paid' || status === 'Commission Paid' || a.amount_arrived >= a.amount_invoiced;
  const shown = settled ? a.amount_arrived : (a.amount_expected ?? a.amount_invoiced - a.amount_arrived);
  return (
    <span data-testid="arrived" data-state={settled ? 'received' : 'expected'} style={{ color: settled ? 'var(--color-status-green)' : 'var(--color-status-red)', fontWeight: 'var(--fw-medium)' }}>
      {money(shown)}{settled ? '' : ' expected'}
    </span>
  );
}

export function useInvoiceAllocations(invoiceId: string | undefined) {
  return useQuery<{ rows: CommissionAllocation[] }>({
    queryKey: ['commission-allocations', 'invoice', invoiceId],
    queryFn: ({ signal }) => api.get<{ rows: CommissionAllocation[] }>(`/api/commissions/splits/invoice/${invoiceId}`, signal),
    enabled: !!invoiceId,
  });
}

/** The invoice's copied split: one line per person, invoiced and arrived, in status colour. */
export function InvoiceCommissionPanel({ rows, status, loading }: { rows: CommissionAllocation[]; status: CommissionStatus; loading?: boolean }) {
  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading commission split…</p>;
  if (rows.length === 0) {
    return (
      <div style={{ padding: 'var(--sp-4)', color: 'var(--color-ink-3)', border: '1px dashed var(--color-border)', borderRadius: 'var(--r-lg)' }}>
        No commission split on this invoice. A split is copied from the property&apos;s consultant deal when an invoice is created.
      </div>
    );
  }
  const td = { padding: 'var(--sp-2) var(--sp-3)', borderBottom: '1px solid var(--color-border)', color: 'var(--color-ink)' } as const;
  const th = { ...td, textAlign: 'left' as const, color: 'var(--color-ink-3)', fontWeight: 'var(--fw-medium)' as const };
  return (
    <table data-testid="invoice-commission" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)', background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)' }}>
      <thead><tr>{['Bucket', 'Person', 'Percent', 'Invoiced', 'Arrived'].map(h => <th scope="col" key={h} style={th}>{h}</th>)}</tr></thead>
      <tbody>
        {rows.map(a => (
          <tr key={a.id} data-testid="allocation">
            <td style={td}>{roleLabel(a)}</td>
            <td style={td}><strong>{a.assignee_name}</strong></td>
            <td style={td}>{a.percent}%</td>
            <td style={td}>{money(a.amount_invoiced)}</td>
            <td style={td}><ArrivedCell a={a} status={status} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
